import { Component, computed, effect, inject, signal, ChangeDetectionStrategy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CapabilityRegistryService } from '../../../service/concierge/capability-registry.service';
import { NavigatorService } from '../../../service/concierge/navigator.service';
import { ConciergeUsageService } from '../../../service/concierge/concierge-usage.service';
import {
  Capability,
  CapabilityCategory,
  ConciergeMessage,
  NavigatorDecision,
} from '../../../service/concierge/capability.types';

type View = 'browse' | 'thinking' | 'confirm' | 'clarify' | 'nomatch';

/**
 * Section order and labels for the empty-state list. Sorting the tail by this
 * rank is what makes each category contiguous — registry order alone interleaves
 * them (a 'navigate' entry sits after the admin block), which would emit the
 * same header twice.
 */
const CATEGORY_ORDER: CapabilityCategory[] = ['navigate', 'create', 'report', 'admin'];
const CATEGORY_LABELS: Record<CapabilityCategory, string> = {
  navigate: 'Go to',
  create: 'Create',
  report: 'Reports',
  admin: 'Admin',
};

/** Confidence at/above which we navigate without asking. */
const AUTO_NAV_THRESHOLD = 0.85;
/** Confidence below which even a single match is treated as "ask first". */
const CONFIRM_FLOOR = 0.5;
/** How many rows the Frequently-Used section shows at most. */
const FREQUENT_LIMIT = 5;
/** Below this many distinct used capabilities we don't show the section at all —
 *  a "Frequently used" header over a single row on day one reads as broken. */
const MIN_DISTINCT_FOR_SECTION = 2;

/**
 * The concierge / command palette. Opens on Cmd-K (see SecureHomeComponent).
 *
 * Two entry paths, serving both personas:
 *   - Power users: type → an instant, local, keyboard-navigable list of matching
 *     capabilities; Enter on one runs it. No network, no latency.
 *   - Everyone: type a full sentence and pick "Ask the assistant" → NavigatorService
 *     resolves it (LLM in Phase 2, local stub in Phase 1) and we auto-navigate on
 *     high confidence / confirm on medium / offer choices when ambiguous.
 */
@Component({
  selector: 'app-concierge-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule],
  template: `
    <div class="concierge">
      <div class="search-row">
        <mat-icon>search</mat-icon>
        <input
          #searchInput
          type="text"
          class="search-input"
          placeholder="What do you want to do today?"
          [ngModel]="query()"
          (ngModelChange)="onQueryChange($event)"
          (keydown)="onKeydown($event)"
          autocomplete="off"
          spellcheck="false" />
        <span class="hint">esc</span>
      </div>

      @switch (view()) {
        @case ('thinking') {
          <div class="state-panel">
            <mat-progress-spinner mode="indeterminate" diameter="28"></mat-progress-spinner>
            <span>Thinking…</span>
          </div>
        }

        @case ('confirm') {
          <div class="state-panel confirm">
            <div class="confirm-text">
              <mat-icon>arrow_forward</mat-icon>
              <div>
                <div class="confirm-title">Go to <strong>{{ decisionCapability()?.title }}</strong>?</div>
                @if (decision()?.message) { <div class="confirm-sub">{{ decision()?.message }}</div> }
              </div>
            </div>
            <div class="confirm-actions">
              <button mat-flat-button color="primary" (click)="runDecision()">Go</button>
              <button mat-button (click)="backToBrowse()">Something else</button>
            </div>
          </div>
        }

        @case ('clarify') {
          <div class="results">
            @if (decision()?.message) { <div class="results-header">{{ decision()?.message }}</div> }
            @for (cap of candidateCaps(); track cap.id; let i = $index) {
              <button
                class="result-row"
                [attr.data-row-index]="i"
                [class.active]="highlightedIndex() === i"
                (mouseenter)="highlightedIndex.set(i)"
                (click)="executeCapability(cap.id)">
                <mat-icon>{{ iconFor(cap) }}</mat-icon>
                <span class="result-title">{{ cap.title }}</span>
                <span class="result-desc">{{ cap.description }}</span>
              </button>
            }
          </div>
        }

        @case ('nomatch') {
          <div class="state-panel nomatch">
            <mat-icon>sentiment_dissatisfied</mat-icon>
            <div>
              <div>{{ decision()?.message || "I couldn't find that." }}</div>
              <button mat-button color="primary" (click)="backToBrowse()">Browse instead</button>
            </div>
          </div>
        }

        @default {
          <div class="results">
            @if (visibleCaps().length === 0 && query().trim()) {
              <div class="results-header muted">No quick matches — try “Ask the assistant”.</div>
            }
            @for (cap of visibleCaps(); track cap.id; let i = $index) {
              @if (sectionHeaders().get(i); as header) {
                <div class="results-header">{{ header }}</div>
              }
              <button
                class="result-row"
                [attr.data-row-index]="i"
                [class.active]="highlightedIndex() === i"
                (mouseenter)="highlightedIndex.set(i)"
                (click)="executeCapability(cap.id)">
                <mat-icon>{{ iconFor(cap) }}</mat-icon>
                <span class="result-title">{{ cap.title }}</span>
                <span class="result-desc">{{ cap.description }}</span>
              </button>
            }
          </div>
          <!-- Outside .results, so it stays visible however far the list is
               scrolled. Kept LAST in keyboard order on purpose: Enter should run
               the best local match, not bounce the query off the resolver. -->
          @if (showAskRow()) {
            <button
              class="result-row ask-row"
              [class.active]="highlightedIndex() === visibleCaps().length"
              (mouseenter)="highlightedIndex.set(visibleCaps().length)"
              (click)="runConcierge()">
              <mat-icon>auto_awesome</mat-icon>
              <span class="result-title">Ask the assistant</span>
              <span class="result-desc">“{{ query().trim() }}”</span>
            </button>
          }
        }
      }
    </div>
  `,
  styles: [`
    /* min() rather than a hard floor: the dialog is capped at 92vw, so a fixed
       520px min-width overflowed horizontally on narrow viewports. */
    .concierge { display: flex; flex-direction: column; min-width: min(520px, 100%); max-width: 640px; }
    .search-row { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid #e0e0e0; }
    .search-row > mat-icon { color: #757575; }
    .search-input { flex: 1; border: none; outline: none; font-size: 18px; background: transparent; color: inherit; }
    /* Bright, obvious greeting. Placeholders auto-hide the instant the user types;
       the :focus rule also clears it the instant they click into the field — so the
       greeting disappears on click OR typing and the box is all about their text. */
    .search-input::placeholder { color: #3f51b5; font-weight: 600; opacity: 1; }
    .search-input:focus::placeholder { opacity: 0; }
    .hint { font-size: 11px; color: #9e9e9e; border: 1px solid #e0e0e0; border-radius: 4px; padding: 1px 6px; }
    .results { display: flex; flex-direction: column; max-height: 380px; overflow-y: auto; padding: 6px; }
    .results-header { padding: 8px 12px 4px; font-size: 12px; color: #616161; font-weight: 500; }
    .results-header.muted { color: #9e9e9e; font-weight: 400; }
    /* Fixed title column (not \`auto\`): each row is its own grid, so an \`auto\`
       track sized itself per-row and the description started at a different x on
       every line. A fixed track lines all three columns up down the whole list. */
    .result-row {
      display: grid; grid-template-columns: 24px 190px 1fr; align-items: center; gap: 12px;
      text-align: left; background: transparent; border: none; cursor: pointer;
      padding: 10px 12px; border-radius: 8px; color: inherit; font: inherit; width: 100%;
    }
    .result-row.active { background: #e8eaf6; }
    .result-row > mat-icon { color: #3f51b5; font-size: 20px; height: 20px; width: 20px; }
    .result-title { font-size: 14px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .result-desc { font-size: 12px; color: #757575; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ask-row { border-top: 1px solid #e0e0e0; margin: 0; border-radius: 0 0 12px 12px; flex: 0 0 auto; }
    .ask-row > mat-icon { color: #00897b; }
    @media (max-width: 560px) {
      .result-row { grid-template-columns: 24px 1fr; }
      .result-desc { display: none; }
    }
    .state-panel { display: flex; align-items: center; gap: 14px; padding: 22px 18px; font-size: 14px; color: #424242; }
    .state-panel.nomatch mat-icon { color: #9e9e9e; }
    .confirm { flex-direction: column; align-items: stretch; }
    .confirm-text { display: flex; gap: 12px; align-items: center; }
    .confirm-text mat-icon { color: #3f51b5; }
    .confirm-title { font-size: 15px; }
    .confirm-sub { font-size: 12px; color: #757575; margin-top: 2px; }
    .confirm-actions { display: flex; gap: 8px; margin-top: 16px; justify-content: flex-end; }
  `],
})
export class ConciergeDialogComponent implements AfterViewInit {
  private registry = inject(CapabilityRegistryService);
  private navigator = inject(NavigatorService);
  private usage = inject(ConciergeUsageService);
  private dialogRef = inject(MatDialogRef<ConciergeDialogComponent>);
  private host = inject(ElementRef<HTMLElement>);
  private data = inject<{ greet?: boolean } | null>(MAT_DIALOG_DATA, { optional: true });

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  constructor() {
    // Keep the keyboard-highlighted row inside the scroll viewport. The list is
    // capped at 380px but holds ~30 rows, so without this, arrowing past the
    // ninth row moved the highlight somewhere the user couldn't see.
    effect(() => {
      const index = this.highlightedIndex();
      this.view(); // re-run when the view swaps and rows are re-created
      // After render: the row for this index may not exist yet on the first pass.
      queueMicrotask(() => {
        const row = this.host.nativeElement.querySelector(`[data-row-index="${index}"]`);
        (row as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
      });
    });
  }

  readonly query = signal('');
  readonly view = signal<View>('browse');
  readonly highlightedIndex = signal(0);
  readonly decision = signal<NavigatorDecision | null>(null);

  /**
   * The user's most-used capabilities, pulled to the top of the empty-state list.
   * Empty while typing (a typed search stays pure relevance) and until the user
   * has actually used a couple of things. Re-filtered through getEnabled() so a
   * capability since disabled by a flag or a lost admin role can't resurface.
   */
  readonly frequentCaps = computed<Capability[]>(() => {
    if (this.query().trim()) return [];
    if (this.usage.distinctCount() < MIN_DISTINCT_FOR_SECTION) return [];
    const enabledIds = new Set(this.registry.getEnabled().map((c) => c.id));
    return this.usage
      .topIds(FREQUENT_LIMIT)
      .filter((id) => enabledIds.has(id))
      .map((id) => this.registry.get(id))
      .filter((c): c is Capability => !!c);
  });

  /** Where the "All actions" divider goes; 0 means no Frequently-Used section. */
  readonly frequentCount = computed(() => this.frequentCaps().length);

  /**
   * Instant, local matches (all enabled capabilities when the box is empty, with
   * the frequently-used ones hoisted to the front). Frequent entries appear ONCE
   * — they're removed from the tail — so this stays a flat list of the same
   * length and the arrow-key index math below needs no special cases.
   */
  readonly visibleCaps = computed<Capability[]>(() => {
    const q = this.query().trim();
    if (q) return this.registry.search(q).map((m) => m.capability);

    const frequent = this.frequentCaps();
    const frequentIds = new Set(frequent.map((c) => c.id));
    const rest = this.registry
      .getEnabled()
      .filter((c) => !frequentIds.has(c.id))
      .sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
    return [...frequent, ...rest];
  });

  /**
   * Row index → the header to draw above it. Empty while the user is typing —
   * a typed search is ranked by relevance, and slicing that into categories
   * would scatter the best matches under four different headings.
   */
  readonly sectionHeaders = computed<Map<number, string>>(() => {
    const headers = new Map<number, string>();
    if (this.query().trim()) return headers;

    const caps = this.visibleCaps();
    const frequent = this.frequentCount();
    if (frequent > 0) headers.set(0, 'Frequently used');

    let previous: CapabilityCategory | null = null;
    for (let i = frequent; i < caps.length; i++) {
      const category = caps[i].category;
      if (category !== previous) {
        headers.set(i, CATEGORY_LABELS[category]);
        previous = category;
      }
    }
    return headers;
  });

  readonly showAskRow = computed(() => this.query().trim().length > 0);

  /** Capabilities behind the decision's candidate ids (for the clarify view). */
  readonly candidateCaps = computed<Capability[]>(() => {
    const cands = this.decision()?.candidates ?? [];
    return cands
      .map((c) => this.registry.get(c.capabilityId))
      .filter((c): c is Capability => !!c);
  });

  /** The single capability a decision points at (for the confirm view). */
  readonly decisionCapability = computed<Capability | undefined>(() => {
    const id = this.decision()?.capabilityId;
    return id ? this.registry.get(id) : undefined;
  });

  ngAfterViewInit(): void {
    // Power-user opens (pill / Cmd-K) autofocus for instant typing. The login
    // "greet" open does NOT autofocus, so the "What do you want to do today?"
    // greeting stays visible until the user clicks or types (see :focus::placeholder).
    if (!this.data?.greet) {
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.highlightedIndex.set(0);
    if (this.view() !== 'browse') this.view.set('browse');
  }

  onKeydown(event: KeyboardEvent): void {
    const count = this.navigableCount();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count > 0) this.highlightedIndex.set((this.highlightedIndex() + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count > 0) this.highlightedIndex.set((this.highlightedIndex() - 1 + count) % count);
        break;
      case 'Enter':
        event.preventDefault();
        this.activateHighlighted();
        break;
    }
  }

  /** How many rows the arrow keys cycle through in the current view. */
  private navigableCount(): number {
    if (this.view() === 'clarify') return this.candidateCaps().length;
    if (this.view() === 'browse') return this.visibleCaps().length + (this.showAskRow() ? 1 : 0);
    return 0;
  }

  private activateHighlighted(): void {
    const idx = this.highlightedIndex();
    if (this.view() === 'clarify') {
      const cap = this.candidateCaps()[idx];
      if (cap) this.executeCapability(cap.id);
      return;
    }
    if (this.view() === 'browse') {
      const caps = this.visibleCaps();
      if (idx < caps.length) {
        this.executeCapability(caps[idx].id);
      } else if (this.showAskRow()) {
        this.runConcierge();
      }
    }
  }

  /** Send the typed sentence to the resolver and apply the confidence tiers. */
  async runConcierge(): Promise<void> {
    const text = this.query().trim();
    if (!text) return;
    this.view.set('thinking');
    const messages: ConciergeMessage[] = [{ role: 'user', content: text }];
    const decision = await this.navigator.resolve(messages);
    this.decision.set(decision);
    this.highlightedIndex.set(0);

    if (!decision.capabilityId) {
      this.view.set('nomatch');
      return;
    }
    const multipleLowConfidence = (decision.candidates?.length ?? 0) > 1 && decision.confidence < CONFIRM_FLOOR;
    if (decision.needsClarification || multipleLowConfidence) {
      this.view.set('clarify');
      return;
    }
    const missingParam = this.hasMissingRequiredParam(decision);
    if (decision.confidence >= AUTO_NAV_THRESHOLD && !missingParam) {
      this.runDecision();
      return;
    }
    this.view.set('confirm');
  }

  /** Execute the current decision's capability (from the confirm view / auto-nav). */
  async runDecision(): Promise<void> {
    const d = this.decision();
    if (!d?.capabilityId) return;
    const ok = await this.navigator.execute(d.capabilityId, d.extractedParams);
    if (ok) this.dialogRef.close();
    else this.view.set('nomatch');
  }

  /** Execute a capability directly (a clicked/keyboard-picked palette row). */
  async executeCapability(id: string, params?: Record<string, any>): Promise<void> {
    const ok = await this.navigator.execute(id, params);
    if (ok) this.dialogRef.close();
  }

  backToBrowse(): void {
    this.view.set('browse');
    this.highlightedIndex.set(0);
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  private hasMissingRequiredParam(d: NavigatorDecision): boolean {
    const cap = d.capabilityId ? this.registry.get(d.capabilityId) : undefined;
    const required = (cap?.params ?? []).filter((p) => p.required);
    if (required.length === 0) return false;
    const provided = d.extractedParams ?? {};
    return required.some((p) => provided[p.name] === undefined || provided[p.name] === null || provided[p.name] === '');
  }

  iconFor(cap: Capability): string {
    // Per-capability icon when the registry declares one; the category fallback
    // exists only so a new entry without an icon still renders something.
    if (cap.icon) return cap.icon;
    switch (cap.category) {
      case 'create': return 'add_circle_outline';
      case 'report': return 'assessment';
      case 'admin': return 'admin_panel_settings';
      default: return 'arrow_forward';
    }
  }
}
